import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { FC, useRef, useState } from "react";

import { Td } from "@/components/Table";
import Text from "@/components/Text";
import { TestingCompetitionResource } from "@/types/resources";
import { RootState } from "@/utils/store";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import ProjectsSequenceBadge from "./ProjectsSequenceBadge";

const LinkButton = styled.button`
  width: 24px;
  height: 24px;
  background: rgba(159, 154, 230, 0.15);
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.purple};
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
  }
`;

const StyledText = styled(Text)`
  margin-top: 4px;
  display: inline-block;
`;

const TeamIcon = styled.img`
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  width: 16px;
  height: 16px;
  object-fit: cover;
  border-radius: 50%;
  margin-right: 6px;
`;

const TeamInfo = styled.div`
  display: flex;
  align-items: center;
`;

const StatusDot = styled.div<{ $status?: string }>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 8px;
  ${({ theme, $status }) =>
    $status === "active" &&
    css`
      background: ${theme.colors.success};
    `}
  ${({ theme, $status }) =>
    $status === "ended" &&
    css`
      background: ${theme.colors.error};
    `}
`;

const StatusHolder = styled.div`
  display: flex;
  align-items: center;
`;

const Participant = styled.div`
  display: flex;
  margin-bottom: 4px;
`;

const Ctr = styled(Text)`
  flex-shrink: 0;
  margin-left: 2px;
`;

const ParticipantsBadgeHolder = styled.div`
  position: relative;
  margin-top: 6px;
`;

const ParticipantsBadge = styled(Text)`
  height: 23px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 50px;
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const ParticipantTooltip = styled.div`
  position: absolute;
  left: 0;
  top: 37px;
  width: 141px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  box-shadow: 0px 12px 20px rgba(34, 34, 34, 0.05);
  border-radius: 4px;
  padding: 8px 12px 4px;
  z-index: 1;
`;

const Triangle = styled.div`
  width: 24px;
  height: 10px;
  position: absolute;
  overflow: hidden;
  top: -10px;
  left: 4px;
  &::after {
    border: 0.5px solid ${({ theme }) => theme.colors.lightGray};
    content: "";
    position: absolute;
    width: 12px;
    height: 10px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: 6px;
    left: 5px;
  }
`;

interface Props {
  project: TestingCompetitionResource;
}

const ProjectsTableRow: FC<Props> = ({ project }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  const teamIcon = useSelector(
    (state: RootState) => state.teams[project.teamId].icon?.versions?.icon
  );

  const { status } = useParams();

  const buttonRef = useRef<HTMLButtonElement>(null);

  useOnClickOutside(buttonRef, () => setShowTooltip(false));

  return (
    <tr>
      <Td>
        {project.projectName}
        <br />
        <StyledText $type="extraSmall" $color="gray" as="span">
          {project.projectExternalId}
        </StyledText>
      </Td>
      <Td>
        <TeamInfo>
          {!!teamIcon && (
            <TeamIcon src={teamIcon} alt={`${project.teamName} icon`} />
          )}
          {project.teamName}
        </TeamInfo>
      </Td>
      <Td>
        <StatusHolder>
          <StatusDot $status={status} />
          {status === "active" ? "Active" : "Closed"}
        </StatusHolder>
      </Td>
      <Td>
        <ProjectsSequenceBadge testSequence={project.testSequence} />
      </Td>
      <Td>{project.totalVariations}</Td>
      <Td>
        {project.participants?.slice(0, 3).map((participant, index) => (
          <Participant key={index}>
            <Text $type="smallHeadline" $color={index === 0 ? "black" : "gray"}>
              {participant.userName}
            </Text>
            <Ctr
              $type="smallHeadline"
              $color={index === 0 ? "success" : "gray"}
            >
              · {participant.ctr ? `${participant.ctr} %` : "-"}
            </Ctr>
          </Participant>
        ))}
        {project.participants?.length > 2 && (
          <ParticipantsBadgeHolder>
            <ParticipantsBadge
              ref={buttonRef}
              as="button"
              $type="smallHeadline"
              onClick={() => setShowTooltip((prev) => !prev)}
            >
              +{project.participants?.length - 3} others
            </ParticipantsBadge>
            {!!showTooltip && (
              <ParticipantTooltip>
                {project.participants?.slice(3).map((participant, index) => (
                  <Participant key={index}>
                    <Text $type="extraSmall">{participant.userName}</Text>
                    <Ctr $type="extraSmall" $color="gray">
                      · {participant.ctr ? `${participant.ctr} %` : "-"}
                    </Ctr>
                  </Participant>
                ))}
                <Triangle />
              </ParticipantTooltip>
            )}
          </ParticipantsBadgeHolder>
        )}
      </Td>
      <Td>
        <LinkButton
          as="a"
          href={`/teams/${project.teamId}/optimisation-projects/${project.projectId}`}
          target="_blank"
        >
          <svg
            width="11"
            height="8"
            viewBox="0 0 11 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M7.17839 0.461605C6.88104 0.173235 6.40622 0.180513 6.11785 0.477861C5.82948 0.775208 5.83676 1.25003 6.13411 1.5384L7.899 3.25H1.5C1.08579 3.25 0.75 3.58579 0.75 4C0.75 4.41421 1.08579 4.75 1.5 4.75H7.899L6.13411 6.46161C5.83676 6.74997 5.82948 7.22479 6.11785 7.52214C6.40622 7.81949 6.88104 7.82677 7.17839 7.5384L10.2718 4.5384C10.4174 4.39714 10.4996 4.2029 10.4996 4C10.4996 3.7971 10.4174 3.60286 10.2718 3.46161L7.17839 0.461605Z"
              fill="currentColor"
            />
          </svg>
        </LinkButton>
      </Td>
    </tr>
  );
};

export default ProjectsTableRow;
